import { Money } from "@upserve/financials";
import { camelizeKeys } from "humps";
import { equals, isNil, join, propOr } from "ramda";
import { TrackJS } from "trackjs";
import { v4 as uuid } from "uuid";

import type { Tip } from "components/gratuity/gratuity_input/Gratuity";
import { getPrettyUrl } from "get_base_uri";
import { parseJSON } from "helpers/ajax_request";
import { ERROR_REPORTING_CONSOLE } from "helpers/error_reporting/error_reporting";
import { getGiftCardBalance } from "interfaces/calculate_totals";
import type { OloCreditCard } from "packages/online_ordering/components/objects/OloCreditCard";
import {
  addGiftCardToList,
  removeGiftCardFromListByIndex,
} from "packages/online_ordering/helpers/cart/gift_card";
import {
  ERROR_MAPPINGS,
  GENERIC_ERROR,
} from "packages/online_ordering/helpers/checkout";
import type { CheckoutStatusState } from "packages/online_ordering/reducers/checkout";
import {
  selectGiftCardRequest,
  selectGiftCards,
} from "packages/online_ordering/selectors/common";
import type {
  BalanceGiftCard,
  GiftCardBalanceRequest,
  GiftCardProps,
  INewCreditCard,
} from "types/reducers/payment";
import { RequestStatus } from "types/requests";

export const UPDATE_CONTACT_INFO =
  "online_ordering/checkout/UPDATE_CONTACT_INFO";
export const UPDATE_NEW_CREDIT_CARD =
  "online_ordering/checkout/UPDATE_NEW_CREDIT_CARD";
export const UPDATE_PAYMENT_TYPE =
  "online_ordering/checkout/UPDATE_PAYMENT_TYPE";
export const UPDATE_ORDER_STATUS =
  "online_ordering/checkout/UPDATE_ORDER_STATUS" as const;
export const UPDATE_ORDER_STATUS_WITH_ERROR =
  "online_ordering/checkout/UPDATE_ORDER_STATUS_WITH_ERROR";
export const ADD_ORDER_CONFIRMATION_DETAILS =
  "online_ordering/checkout/ADD_ORDER_CONFIRMATION_DETAILS";
export const PROCEED_TO_CHECKOUT =
  "online_ordering/checkout/PROCEED_TO_CHECKOUT";
export const RETURN_TO_MENU = "online_ordering/checkout/RETURN_TO_MENU";
export const UPDATE_DELIVERY_INSTRUCTIONS =
  "online_ordering/checkout/UPDATE_DELIVERY_INSTRUCTIONS";
export const UPDATE_IS_CURBSIDE_PICKUP =
  "online_ordering/checkout/UPDATE_IS_CURBSIDE_PICKUP";
export const UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS =
  "online_ordering/checkout/UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS";
export const APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE =
  "online_ordering/checkout/APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE";
export const SET_PAYMENT_GIFT_CARD_REQUEST =
  "online_ordering/checkout/SET_PAYMENT_GIFT_CARD_REQUEST";
export const SET_PAYMENT_GIFT_CARD_INFO =
  "online_ordering/checkout/SET_PAYMENT_GIFT_CARD_INFO";
export const UPDATE_TIP = "online_ordering/checkout/UPDATE_TIP";

function updateContactInfo(contactInfo) {
  return {
    type: UPDATE_CONTACT_INFO,
    contactInfo,
  };
}

function updateNewCreditCard(newCreditCard: Partial<INewCreditCard>) {
  return {
    type: UPDATE_NEW_CREDIT_CARD,
    newCreditCard,
  };
}

function updatePaymentType(cards: OloCreditCard[] | undefined, paymentType) {
  return {
    type: UPDATE_PAYMENT_TYPE,
    paymentType,
    cards,
  };
}

function updateOrderStatus(status: CheckoutStatusState["orderStatus"]) {
  return {
    type: UPDATE_ORDER_STATUS,
    status,
  };
}

export type UpdateOrderStatusAction = ReturnType<typeof updateOrderStatus>;

export function updateOrderStatusWithError(
  errorSource: OrderErrorSource,
  error,
  menuError: boolean
) {
  return {
    type: UPDATE_ORDER_STATUS_WITH_ERROR,
    error,
    errorSource,
    menuError,
  };
}

type OrderErrorSource = "submitOrder";
function parseOrderError(
  errorSource: OrderErrorSource,
  response?: Response,
  fallbackMessage = GENERIC_ERROR
) {
  return dispatch => {
    return new Promise<Response>((resolve, reject) =>
      response ? resolve(response) : reject(new Error())
    )
      .then(parseJSON)
      .then(camelizeKeys)
      .then(({ error, errors, errorType }) => {
        let mappedError = fallbackMessage;
        if (equals("submitOrder", errorSource) && !isNil(errors)) {
          // errors will be an array of errors of string
          const orderErrors = errors as string[];
          // For now join the responses as they are already in sentence format.
          mappedError = join(" ", orderErrors);
        } else {
          // Handle regular error response
          // @ts-expect-error TODO: fix and validate this type
          mappedError = propOr(error, error, ERROR_MAPPINGS); // FIXME - Improve error mapping - See HQ-4459
        }
        dispatch(
          updateOrderStatusWithError(
            errorSource,
            mappedError,
            equals("menu", errorType)
          )
        );
        ERROR_REPORTING_CONSOLE.error(mappedError, {
          error,
          errors,
          errorType,
        });
      })
      .catch(() => {
        dispatch(
          updateOrderStatusWithError(
            errorSource,
            "Sorry, online ordering is temporarily unavailable.",
            false
          )
        );
      });
  };
}

export const parseOrderSubmissionError = parseOrderError.bind(
  null,
  "submitOrder"
);

function addOrderConfirmationDetails(orderCompletionDetails) {
  return {
    type: ADD_ORDER_CONFIRMATION_DETAILS,
    orderCompletionDetails,
  };
}

function updateDeliveryInstructions(instructions) {
  return {
    type: UPDATE_DELIVERY_INSTRUCTIONS,
    instructions,
  };
}

function updateIsCurbsidePickup(isCurbsidePickup) {
  return {
    type: UPDATE_IS_CURBSIDE_PICKUP,
    isCurbsidePickup,
  };
}

function updateCurbsidePickupInstructions(instructions) {
  return {
    type: UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS,
    instructions,
  };
}

function proceedToCheckout(paymentOptions) {
  return {
    type: PROCEED_TO_CHECKOUT,
    paymentOptions,
  };
}

function returnToMenu() {
  return {
    type: RETURN_TO_MENU,
  };
}

function applyOrderAddressFromLocalStorage(address) {
  return {
    type: APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE,
    address,
  };
}

function setPaymentGiftCardRequestStatus(status: RequestStatus) {
  return {
    type: SET_PAYMENT_GIFT_CARD_REQUEST,
    status,
  };
}

/**
 * TODO: Move to React Query please.
 * @param status
 */
export function setPaymentGiftCardRequestStatusIfRequesting(
  status: RequestStatus
) {
  return (dispatch, getState) => {
    const request = selectGiftCardRequest(getState());
    if (request.giftCardsRequest === RequestStatus.REQUESTING) {
      dispatch(setPaymentGiftCardRequestStatus(status));
    }
  };
}

function SetPaymentGiftCardInfo(giftCards: Array<BalanceGiftCard>) {
  return {
    type: SET_PAYMENT_GIFT_CARD_INFO,
    giftCards,
  };
}

function applyGiftCard(newGiftCard: GiftCardProps) {
  const { index, giftCardNumber, pin } = newGiftCard;
  let giftCard: BalanceGiftCard = {
    index,
    giftCardNumber,
    balance: Money(0),
    pin,
  };
  const requestId = uuid();
  return (dispatch, getState) => {
    dispatch(setPaymentGiftCardRequestStatus(RequestStatus.REQUESTING));
    const giftCardForBalanceRequest: GiftCardBalanceRequest = {
      giftCode: giftCard.giftCardNumber,
      pin: giftCard.pin,
      storePrettyUrl: getPrettyUrl(),
      requestId,
    };
    getGiftCardBalance(giftCardForBalanceRequest)
      .then(({ balance }) => {
        giftCard = {
          ...giftCard,
          balance: Money(balance),
        };
        const { giftCards } = selectGiftCards(getState());
        dispatch(
          SetPaymentGiftCardInfo(addGiftCardToList(giftCard, giftCards))
        );
      })
      .catch(error => {
        dispatch(setPaymentGiftCardRequestStatus(RequestStatus.FAILURE));
        TrackJS.track(
          `error fetching balance for gift card - request id: ${requestId}`
        );
        TrackJS.track(`ubergateway error response: ${error}`);
      });
  };
}

function removeGiftCard(index: number) {
  return (dispatch, getState) => {
    dispatch(setPaymentGiftCardRequestStatus(RequestStatus.REQUESTING));
    const { giftCards } = selectGiftCards(getState());
    dispatch(
      SetPaymentGiftCardInfo(removeGiftCardFromListByIndex(index, giftCards))
    );
  };
}

export function updateTip(tip: Tip | null) {
  return {
    type: UPDATE_TIP,
    tip,
  };
}

export {
  applyGiftCard,
  applyOrderAddressFromLocalStorage,
  addOrderConfirmationDetails,
  proceedToCheckout,
  removeGiftCard,
  returnToMenu,
  setPaymentGiftCardRequestStatus,
  updateContactInfo,
  updateCurbsidePickupInstructions,
  updateDeliveryInstructions,
  updateIsCurbsidePickup,
  updateNewCreditCard,
  updateOrderStatus,
  updatePaymentType,
};
